import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { api } from '@/shared/api/client';
import { resetHomeEntrance } from '@/features/home/homeEntrance';
import { endErpSession } from '@/shared/erpSession';

// Client half of the idle timeout (the server enforces the same window on
// sessions.last_used_at): after IDLE_LIMIT_MS without user activity the app
// signs out; a countdown toast warns for the final two minutes. Activity is
// shared across tabs via localStorage so one busy tab keeps them all alive.
const IDLE_LIMIT_MS = 30 * 60_000;
const WARN_MS = 2 * 60_000;
const LS_KEY = 'mrpcomp:last-activity';

function lastActivity(): number {
  const v = Number(localStorage.getItem(LS_KEY));
  return Number.isFinite(v) && v > 0 ? v : Date.now();
}

export function IdleLogoutWatcher({ active }: { active: boolean }) {
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);
  const touchedServer = useRef(0);

  useEffect(() => {
    if (!active) return;
    localStorage.setItem(LS_KEY, String(Date.now()));
    let ticking = 0;
    const mark = () => {
      const now = Date.now();
      if (now - ticking < 5_000) return; // throttle writes
      ticking = now;
      localStorage.setItem(LS_KEY, String(now));
    };
    const events: Array<keyof WindowEventMap> = ['pointerdown', 'keydown', 'wheel', 'touchstart'];
    events.forEach((e) => window.addEventListener(e, mark, { passive: true }));

    const iv = window.setInterval(() => {
      const idle = Date.now() - lastActivity();
      if (idle >= IDLE_LIMIT_MS) {
        window.clearInterval(iv);
        void (async () => {
          endErpSession(); // D1: the next user never replays this session's pages
          try { await api.post('/auth/logout'); } catch { /* session may already be gone */ }
          resetHomeEntrance();
          window.location.href = '/login?reason=idle';
        })();
        return;
      }
      if (idle >= IDLE_LIMIT_MS - WARN_MS) {
        setSecondsLeft(Math.max(0, Math.ceil((IDLE_LIMIT_MS - idle) / 1000)));
      } else {
        setSecondsLeft(null);
      }
    }, 1000);

    return () => {
      events.forEach((e) => window.removeEventListener(e, mark));
      window.clearInterval(iv);
    };
  }, [active]);

  const stay = () => {
    localStorage.setItem(LS_KEY, String(Date.now()));
    setSecondsLeft(null);
    // Refresh the server-side last_used_at immediately (throttled to 30s).
    if (Date.now() - touchedServer.current > 30_000) {
      touchedServer.current = Date.now();
      void api.get('/auth/me').catch(() => {});
    }
  };

  if (secondsLeft == null) return null;
  const mm = Math.floor(secondsLeft / 60);
  const ss = String(secondsLeft % 60).padStart(2, '0');
  return createPortal(
    <div className="fixed bottom-4 right-4 z-system w-80 rounded-xl border border-[#C2D8F2] bg-[#E0ECFB] shadow-xl p-3 animate-dropdown">
      <div className="text-[0.8125rem] font-semibold text-[#2D5596]">Still there?</div>
      <div className="mt-0.5 text-body text-[#2D5596]">
        You'll be signed out in <span className="font-numeric font-semibold">{mm}:{ss}</span> due to inactivity.
      </div>
      <button
        type="button"
        onClick={stay}
        className="mt-2 inline-flex items-center h-7 px-3 rounded-full bg-[#2D5596] text-white text-[0.75rem] font-medium hover:bg-[#24477D] transition"
      >
        Stay signed in
      </button>
    </div>,
    document.body,
  );
}
